import { captureMessage, withScope } from "@sentry/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowRight } from "@/assets/icons/ds";
import OpenArrow from "@/assets/ux/ArrowOpenDoc.svg?react";
import { Button } from "@/components/Interface/Button/Button";
import { TruncatedText } from "@/components/Interface/TruncatedText/TruncatedText";
import { TableRow } from "@/components/Layout/Table/TableRow/TableRow";
import { BillListCheckbox } from "@/components/Portal/Documents/BillList/components/BillListCheckbox/BillListCheckbox";
import {
  BillStatusChip,
  billStatusConfig,
  isBillStatus,
} from "@/components/Portal/Documents/BillList/components/BillStatusChip/BillStatusChip";
import { useHandleError } from "@/hooks/useHandleError";
import { getBillDocument } from "@/services/api";
import { DrBill } from "@/services/model";
import { downloadDocument } from "@/utils/downloadDocument";
import { BillStatus } from "@/utils/enums";
import { getCurrency, getShortDateString } from "@/utils/translationHelpers";

export const getValidStatus = (status: string) => {
  if (isBillStatus(status)) {
    return billStatusConfig[status].translationKey;
  }

  withScope((scope) => {
    scope.setTag("section", "portal");
    scope.setTag("domain", "documents");
    scope.setContext("status", { status: status ?? "undefined" });
    captureMessage("Received unexpected billing status.");
  });

  return billStatusConfig[BillStatus.closed].translationKey;
};

export interface BillListRowProps {
  bill: DrBill;
  variant?: "default" | "compact";
  isSelectMultiple?: boolean;
}

export const BillListRow = ({
  bill,
  variant = "default",
  isSelectMultiple,
}: BillListRowProps) => {
  const {
    id,
    invoiceDate: date,
    custom1: name,
    billNumber,
    amount,
    status,
  } = bill;
  const { t } = useTranslation("portal");

  const [isLoading, setIsLoading] = useState(false);
  const handleError = useHandleError();

  const downloadBill = () => {
    setIsLoading(true);
    if (id) {
      getBillDocument(id)
        .then((data) =>
          downloadDocument(data.billFile, { pdfFilenamePrefix: "DR_Rechnung" }),
        )
        .catch((error) => handleError(error))
        .finally(() => setIsLoading(false));
    }
  };

  if (!id) {
    return null;
  }

  const statusChip = status ? (
    <BillStatusChip
      status={status}
      label={t(`bills.list.status.${getValidStatus(status)}`)}
    />
  ) : null;

  if (variant === "compact") {
    // Mobile row: the left column is the tariff name over a single line holding
    // date, invoice number, status chip and — right-aligned — the amount. A
    // trailing column holds the open button, which the row checkbox takes over
    // while selecting.
    // The download button shows its own loading state, so no page spinner.
    return (
      <div data-testid="mobile-bill-list-row">
        <div className="flex h-16 items-center border-b border-border-tertiary">
          <div className="flex min-w-0 flex-1 flex-col gap-1 px-2">
            <TruncatedText
              className="text-body-sm font-semibold leading-md text-foreground-primary"
              text={name ?? ""}
            />
            {/* Date, status chip and amount on one line — the invoice
                  number only appears in the desktop table. Date/chip are
                  short and the row is wide, so however this leftover space
                  is handled, it shows up somewhere: packing everything left
                  (no spacers) put it all after the amount, right before the
                  button, which read as a wide gap between the amount and the
                  checkbox/open column; pushing the amount flush against that
                  column instead (`ml-auto` on just the amount) only moved
                  the same gap to before the amount, between the chip and the
                  sum. Splitting it across two spacers — one before and one
                  after the amount — halves however big that gap is instead
                  of concentrating it in either single spot. */}
            <div className="flex items-center justify-start text-body-xs text-foreground-primary">
              <span className="flex-1">
                {date && getShortDateString(new Date(date))}
              </span>
              {statusChip && <span className="flex-1">{statusChip}</span>}
              <span className="flex-1">
                {/* getCurrency joins the code and value with a non-breaking
                      space (Intl.NumberFormat's de-CH output), which blocks
                      any wrap there even once this span is squeezed - so
                      "CHF 56.00" overflowed past its box onto the button
                      instead of breaking onto a second line. Swap it for a
                      normal space so text-wrap can break "CHF" / "56.00"
                      when there isn't room for both on one line. */}
                {getCurrency(amount || 0, "CHF").replace(" ", " ")}
              </span>
            </div>
          </div>
          {/* Trailing column: a slot for the open button, another for the
                checkbox. Note the mobile open button uses the plain
                arrow-right glyph, not the desktop's arrow-square-out. */}
          {isSelectMultiple ? (
            <div className="flex w-10 shrink-0 justify-center">
              <BillListCheckbox id={id} />
            </div>
          ) : (
            <div className="flex w-12 shrink-0 justify-center">
              <Button
                dataTestid="mobile-bill-list-row-download"
                variant="secondary"
                size="sm"
                type="button"
                loading={isLoading}
                ariaLabel={t("bills.list.downloadBill")}
                icon={<ArrowRight className="size-4" />}
                onClick={downloadBill}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // No page spinner here either: a <div> inside <tbody> is invalid, and the
  // download button already shows the loading state.
  return (
    <TableRow>
      <td>{date && getShortDateString(new Date(date))}</td>
      <td className="bill-list__name">
        <TruncatedText text={name ?? ""} />
      </td>
      <td>{billNumber}</td>
      {/* // TODO: Insert dynamic currency id here */}
      <td>{getCurrency(amount || 0, "CHF")}</td>
      <td>{statusChip ?? "-"}</td>
      {/* The trailing column swaps: the checkbox takes the place of
            "Öffnen" while selecting, on both breakpoints. */}
      {isSelectMultiple ? (
        <td className="bill-selection">
          <BillListCheckbox id={id} />
        </td>
      ) : (
        <td>
          <Button
            dataTestid="bill-download-icon"
            variant="secondary"
            size="sm"
            type="button"
            loading={isLoading}
            ariaLabel={t("bills.list.downloadBill")}
            icon={<OpenArrow className="size-4" />}
            onClick={downloadBill}
          />
        </td>
      )}
    </TableRow>
  );
};
